import {useCallback, useEffect, useRef, useState} from 'react';
import {Box, CircularProgress, Divider, Drawer, FormControl, FormHelperText, Grid, MenuItem, Snackbar, Stack, Tab, Tabs, TextField, Tooltip, Typography} from '@mui/material';
import {styled} from '@mui/styles';
import PropTypes from "prop-types";
import {useFieldArray, useForm, useWatch} from "react-hook-form"
import {flexStyles} from '../../../../../component/theme/theme';
import {DatePicker, LocalizationProvider, TimePicker} from '@mui/x-date-pickers';
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import {useParams} from 'react-router';
import {Add, DeleteOutline} from '@mui/icons-material';
import {addShipment, addVerification, deleteShipment, deleteVerification, fetchAllCustomerContacts, fetchVerificationsDetails, patchShipment, patchVerification} from "../../../../../utils/apiCalls";
import {INGESTION_SITES} from "../../../../../utils/constants";
import Switch from "@mui/material/Switch";
import {addressValidationRules, cityValidationRules, consignmentNumberValidationRules, countryValidationRules, descriptionMessageValidationRules, emailValidationRules, firstNameValidationRules, ingestionSiteValidationRules, lastNameValidationRules, phoneNumberValidationRules, postalCodeValidationRules, stateValidationRules, trackingLinkValidationRules, twoDecimalPrecisionNumberValidationRules} from "../../../../../utils/validationRules";
import {StyledButtonContained, StyledButtonOutlined, useStyles} from "../../../../../utils/uiUtils";
import {AddressBadge} from "../../../../../component/address/AddressBadge";
import {AddressPicker} from "../../../../../component/address/AddressPicker";
import {UserBadge} from "../../../../../component/user/UserBadge";
import {UserPicker} from "../../../../../component/user/UserPicker";

const getShipmentStage = (status) => {
    const STATUS_MAP = {
        preparing_for_shipment: 0,
        courier_en_route: 1,
        shipment_collected: 2,
        out_for_delivery: 3,
        verified_and_completed: 4,
    };
    return STATUS_MAP[status] ?? -1;
}

const CustomHTabList = styled(Tabs)(() => ({
    borderRadius: '12px 0px 0px 12px',
    '& .MuiTabs-scroller': {
        background: '#3A3E3F',
    },
    '& .MuiTabs-indicator': {
        background: '#FFC540 !important',
    },
    '& .MuiTab-root': {
        color: '#FFFFFF',
    },
    textTransform: 'none',
}));

const CustomHTab = styled(Tab)(() => ({
    textTransform: 'none !important',
    fontFamily: 'Poppins !important',
    color: '#FFFFFF !important',
    '&.Mui-selected': {
        color: '#FFC540 !important',
    }
}));


function TabPanel(props) {
    const {children, value, index, ...other} = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && (
                <>{children}</>
            )}
        </div>
    );
}

TabPanel.propTypes = {
    children: PropTypes.node,
    index: PropTypes.number.isRequired,
    value: PropTypes.number.isRequired,
};

export const AdminShippingManagement = ({shipments, customer, refreshShipmentDetails, order}) => {
    const params = useParams();
    const [orderId, setOrderId] = useState(params.orderId);

    const [state, setState] = useState({
        openSb: false,
        vertical: 'top',
        horizontal: 'right',
    });
    const [snackbarMessage, setSnackbarMessage] = useState("");

    const {vertical, horizontal, openSb} = state;

    const openSnackbar = (message) => {
        setSnackbarMessage(message)
        setState({
            vertical: 'top', horizontal: 'right', openSb: true
        });
    };

    const closeSnackbar = () => {
        setState({
            vertical: 'top', horizontal: 'right', openSb: false
        });
    };

    const [newShipmentEnabled, setNewShipmentEnabled] = useState(false);
    const [customerUsers, setCustomerUsers] = useState({platform: [], contacts: []});

    useEffect(() => {
        if (customer?.uuid) {
            fetchAllCustomerContacts(customer.uuid)
                .then(result => setCustomerUsers(result))
                .catch(() => {});
        }
    }, [customer?.uuid]);
    const {register, handleSubmit, watch, formState: {errors}, setValue, control, getValues, reset, trigger} = useForm();
    const {
        register: registerNewShipment,
        handleSubmit: handleSubmitNewShipment,
        watch: watchNewShipment,
        formState: {errors: errorsNewShipment},
        setValue: setValueNewShipment,
        control: controlNewShipment,
        getValues: getValuesNewShipment,
        reset: resetNewShipment,
        trigger: triggerNewShipment
    } = useForm();

    const [isDefaultLoaded, setIsDefaultLoaded] = useState(false);
    const initialStage = useRef({});

    const [expectedMessage, setExpectedMessage] = useState({error: false, message: ""});

    const initializeForm = () => {
        let defaultValues = {};

        defaultValues = {
            ...defaultValues,
            [`externalShipmentDescription`]: order.external_pickup_description ?? "",
        }

        shipments?.map((shipment, index) => {
            const shipmentStatus = (shipment.status) ? shipment.status : 'preparing_for_shipment';
            initialStage.current[index] = shipmentStatus;

            defaultValues = {
                ...defaultValues,
                [`shipmentStatus-${index}`]: shipmentStatus,
                [`contactFirstName-${index}`]: shipment.first_name ?? "",
                [`contactLastName-${index}`]: shipment.last_name ?? "",
                [`contactMobileNumber-${index}`]: shipment.mobile_number ?? "",
                [`contactPhoneNumber-${index}`]: shipment.phone_number ?? "",
                [`contactEmail-${index}`]: shipment.optional_email ?? "",
                [`shipmentAddress_line_1-${index}`]: shipment.shipping_address?.address_line_1 ?? "",
                [`shipmentAddress_line_2-${index}`]: shipment.shipping_address?.address_line_2 ?? "",
                [`shipmentPostalCode-${index}`]: shipment.shipping_address?.postal_code ?? "",
                [`shipmentCity-${index}`]: shipment.shipping_address?.city ?? "",
                [`shipmentState-${index}`]: shipment.shipping_address?.state ?? "",
                [`shipmentCountry-${index}`]: shipment.shipping_address?.country ?? "",
                [`shipmentIngestionSite-${index}`]: shipment.ingestion_site ?? "perth",
                [`selfShipmentTrackingLink-${index}`]: shipment.tracking_link ?? ""
            }

            const formatedPickupContactNumber = shipment.pickup_contact_number ? shipment.pickup_contact_number.replace(" ", "").replace(/\B(?=(\d{3})+(?!\d))/g, ' ') : "";
            if (getShipmentStage(shipmentStatus) >= 0) {
                // Pre-fill pickup contact from initial details if empty
                const pickupContact = formatedPickupContactNumber || shipment.mobile_number || shipment.phone_number || "";
                defaultValues = {
                    ...defaultValues,

                    [`pickupConsignmentNumber-${index}`]: shipment.pickup_consignment_number,
                    [`pickupConsignmentContact-${index}`]: pickupContact,
                }
            }

            if (getShipmentStage(shipmentStatus) >= 1) {
                let pickupTime;
                if (shipment.pickup_arrival_time) {
                    pickupTime = `2023-11-21T${shipment.pickup_arrival_time}.000000Z`;
                } else {
                    pickupTime = `2023-11-21T00:00:00.000000Z`;
                }
                defaultValues = {
                    ...defaultValues,

                    [`pickupNumberOfBoxes-${index}`]: shipment.pickup_box_count,
                    [`pickupWeight-${index}`]: shipment.pickup_weight,
                    [`pickupDateOfArrival-${index}`]: shipment.pickup_arrival_date,
                    [`pickupTime-${index}`]: pickupTime,
                }
            }

            const formatedDeliveryContactNumber = shipment.delivery_contact_number ? shipment.delivery_contact_number.replace(" ", "").replace(/\B(?=(\d{3})+(?!\d))/g, ' ') : "";
            if (getShipmentStage(shipmentStatus) >= 2) {
                // Pre-fill delivery contact from pickup contact if empty
                const deliveryContact = formatedDeliveryContactNumber || defaultValues[`pickupConsignmentContact-${index}`] || "";
                defaultValues = {
                    ...defaultValues,

                    [`deliveryConsignmentNumber-${index}`]: shipment.delivery_consignment_number,
                    [`deliveryContact-${index}`]: deliveryContact,
                }
            }

            if (getShipmentStage(shipmentStatus) >= 3) {
                // Pre-fill shipping box count from pickup box count if empty
                const shipmentBoxCount = shipment.shipment_box_count || defaultValues[`pickupNumberOfBoxes-${index}`] || "";
                defaultValues = {
                    ...defaultValues,

                    [`shipmentConsignmentNumber-${index}`]: shipment.shipment_consignment_number,
                    [`shippingReceivedOn-${index}`]: shipment.shipment_received_on,
                    [`shipmentPackaging-${index}`]: shipment.shipment_packaging ?? "boxes",
                    [`shipmentNoOfBoxes-${index}`]: shipmentBoxCount,
                    [`shipmentDescription-${index}`]: shipment.shipment_description,
                    [`shipmentCarrier-${index}`]: shipment.courier_partner,

                    [`verificationTotalTapes-${index}`]: shipment.verification_tape_count,

                }
            }
        });
        reset(defaultValues);
        setIsDefaultLoaded(true);
    };

    useEffect(() => {
        // On mount populate forms with default values, if any
        initializeForm();
    }, [shipments]);

    const initializeNewShipmentForm = () => {
        let defaultValues = {};

        defaultValues = {
            ...defaultValues,
            [`externalShipmentDescription`]: order.external_pickup_description ?? "",

            [`newShipmentContactFirstName`]: "",
            [`newShipmentContactLastName`]: "",
            [`newShipmentContactMobileNumber`]: "",
            [`newShipmentContactPhoneNumber`]: "",
            [`newShipmentContactEmail`]: "",
            [`newShipmentAddress_line_1`]: "",
            [`newShipmentAddress_line_2`]: "",
            [`newShipmentPostalCode`]: "",
            [`newShipmentCity`]: "",
            [`newShipmentState`]: "",
            [`newShipmentCountry`]: "",
            [`newShipmentIngestionSite`]: "perth",
            [`newSelfShipmentTrackingLink`]: ""
        };

        resetNewShipment(defaultValues);
        setIsDefaultLoaded(true);
    };

    useEffect(() => {
        if (newShipmentEnabled) {
            initializeNewShipmentForm();
        }
    }, [newShipmentEnabled]);


    const classes = useStyles();

    const getStatusSectionText = (status) => {
        switch (status) {
            case 'courier_en_route':
                return 'Pickup Information';
            case 'shipment_collected':
                return 'Confirm Receipt';
            case 'out_for_delivery':
                return 'Delivery Information';
            case 'verified_and_completed':
                return 'Shipment Completed';
            default:
                return '';
        }
    }

    const closeNewShipmentAddForm = () => {
        initializeNewShipmentForm();
        setNewShipmentEnabled(false);
    };

    const [shipmentTab, setShipmentTab] = useState(0);
    const handleShipmentTabChange = (event, newValue) => {
        initializeForm();
        setShipmentTab(newValue);
        closeNewShipmentAddForm();
    };

    const onSubmit = async (data) => {
        const shipmentStatus = data[`shipmentStatus-${shipmentTab}`];
        if (shipmentStatus) {
            // Auto-progress: if not at final stage, advance status before saving
            const currentStageIndex = STATUS_SEQUENCE.findIndex(s => s.value === shipmentStatus);
            const isLast = currentStageIndex === STATUS_SEQUENCE.length - 1;
            const nextStatus = isLast ? shipmentStatus : STATUS_SEQUENCE[currentStageIndex + 1].value;

            let payload = {
                status: nextStatus,
            }

            if (getShipmentStage(shipmentStatus) >= 1) {
                payload = {
                    ...payload,

                    pickup_consignment_number: data[`pickupConsignmentNumber-${shipmentTab}`],
                    pickup_contact_number: !data[`pickupConsignmentContact-${shipmentTab}`] ? null : data[`pickupConsignmentContact-${shipmentTab}`],
                }
            }

            if (getShipmentStage(shipmentStatus) >= 2) {
                payload = {
                    ...payload,

                    pickup_box_count: !data[`pickupNumberOfBoxes-${shipmentTab}`] ? null : data[`pickupNumberOfBoxes-${shipmentTab}`],
                    pickup_weight: Number(data[`pickupWeight-${shipmentTab}`]),
                    pickup_arrival_date: data[`pickupDateOfArrival-${shipmentTab}`]?.slice(0, 10),
                    pickup_arrival_time: data[`pickupTime-${shipmentTab}`]?.slice(11, 19),
                }
            }

            if (getShipmentStage(shipmentStatus) >= 3) {
                payload = {
                    ...payload,

                    delivery_consignment_number: data[`deliveryConsignmentNumber-${shipmentTab}`],
                    delivery_contact_number: !data[`deliveryContact-${shipmentTab}`] ? null : data[`deliveryContact-${shipmentTab}`],
                }
            }

            if (getShipmentStage(shipmentStatus) >= 4) {
                payload = {
                    ...payload,

                    shipment_consignment_number: data[`shipmentConsignmentNumber-${shipmentTab}`],
                    shipment_received_on: data[`shippingReceivedOn-${shipmentTab}`]?.slice(0, 10),
                    shipment_packaging: data[`shipmentPackaging-${shipmentTab}`] !== "selectOption" ? data[`shipmentPackaging-${shipmentTab}`] : null,
                    shipment_box_count: data[`shipmentNoOfBoxes-${shipmentTab}`],
                    shipment_description: data[`shipmentDescription-${shipmentTab}`],

                    verification_tape_count: data[`verificationTotalTapes-${shipmentTab}`],
                }
            }

            patchShipment(orderId, shipments[shipmentTab].uuid, payload, {customerId: customer?.uuid})
                .then(response => {
                    // Saving Verifications, if any
                    if (getShipmentStage(response.status) >= 4) {
                        const shipmentTabVerifications = watch(`verifications-${shipments[shipmentTab]?.uuid}`);
                        if (shipmentTabVerifications?.length > 1 ||
                            (shipmentTabVerifications?.length > 0
                                && shipmentTabVerifications[0].box_number !== ''
                                && shipmentTabVerifications[0].tape_count !== '')) {

                            shipmentTabVerifications.map((v, i) => {
                                let verificationPayload = {
                                    'shipment': shipments[shipmentTab]?.uuid,
                                    'box_number': v.box_number,
                                    'tape_count': v.tape_count ?? 0,
                                    'comments': v.comments,
                                    'has_transmittal': v.has_transmittal,
                                }

                                const uuid = watch(`verifications-${shipments[shipmentTab]?.uuid}[${i}].uuid`);
                                if (uuid) {
                                    patchVerification(orderId, shipments[shipmentTab]?.uuid, uuid, verificationPayload)
                                        .then(response => openSnackbar('Verification updated successfully'))
                                        .catch((err) => {
                                            openSnackbar('Error while updating Verification.');
                                            console.log(err);
                                        });
                                } else {
                                    addVerification(orderId, shipments[shipmentTab]?.uuid, verificationPayload)
                                        .then(response => {
                                            openSnackbar('Verification created successfully');
                                            setValue(`verifications-${shipments[shipmentTab]?.uuid}[${i}].uuid`, response.uuid);
                                        })
                                        .catch((err) => {
                                            openSnackbar('Error while creating Verification.');
                                            console.log(err);
                                        });
                                }
                            });
                        }
                    }

                    openSnackbar('Shipment updated successfully');
                    refreshShipmentDetails();
                })
                .catch(err => console.log(err));

        }
    }

    const mapShippingModeText = (mode) => {
        const shippingMode = {
            'self': 'Self',
            'tapeark': 'Tape Ark',
            'none': 'External'
        };
        return shippingMode[mode];
    };

    const validatePickupInformation = (index) => {
        return Promise.all([
            trigger(`pickupConsignmentNumber-${index}`),
            trigger(`pickupConsignmentContact-${index}`)
        ]).then(([
                     consignmentNumberIsValid,
                     contactNumberIsValid
                 ]) => {
            return consignmentNumberIsValid && contactNumberIsValid
        });
    };

    const validateConfirmReceipt = (index) => {
        return Promise.all([
            trigger(`pickupWeight-${index}`)
        ]).then(([
                     weightIsValid
                 ]) => {
            return weightIsValid
        });
    };

    const validateDeliveryInformation = (index) => {
        return Promise.all([
            trigger(`deliveryConsignmentNumber-${index}`),
            trigger(`deliveryContact-${index}`)
        ]).then(([
                     consignmentNumberIsValid,
                     contactNumberIsValid
                 ]) => {
            return consignmentNumberIsValid && contactNumberIsValid
        });
    };

    const STATUS_SEQUENCE = [
        {
            value: 'preparing_for_shipment',
            label: 'Preparing for Shipment',
        },
        {
            value: 'courier_en_route',
            label: 'Courier En Route',
            validateBeforeNext: idx => validatePickupInformation(idx),
        },
        {
            value: 'shipment_collected',
            label: 'Shipment Collected',
            validateBeforeNext: idx => validateConfirmReceipt(idx),
        },
        {
            value: 'out_for_delivery',
            label: 'Out For Delivery',
            validateBeforeNext: idx => validateDeliveryInformation(idx),
        },
        {
            value: 'verified_and_completed',
            label: 'Verified & Completed',
            // no validate needed for final
        },
    ];

    const renderExternalPickupInformation = () => {
        return (
            <Grid item xs={12} sx={flexStyles.flexColumnStart}>
                <Typography align='left' className={classes.fieldLabel}>Description</Typography>
                <TextField multiline rows={3} className={classes.selectLabelGreyDiff} id="outlined-basic" sx={{width: '100%', opacity: 0.5, pointerEvents: 'none'}}
                           variant="outlined"
                           name={`externalShipmentDescription`}
                           defaultValue={watch(`externalShipmentDescription`)}
                />
            </Grid>
        );
    };

    /**
     * Renders the Tape Ark Initial Details of the shipment or empty fields for adding a new shipment
     * @param {number | undefined} index - The shipment index or undefined for new shipment to add
     * @returns {Element}
     */
    const renderTapeArkInitialDetails = (index = undefined) => {
        const hasContactFirstName = index !== undefined ? watch(`contactFirstName-${index}`) !== "" : true;
        const hasContactLastName = index !== undefined ? watch(`contactLastName-${index}`) !== "" : true;
        const hasContactMobileNumber = index !== undefined ? watch(`contactMobileNumber-${index}`) !== "" : true;
        const hasContactPhoneNumber = index !== undefined ? watch(`contactPhoneNumber-${index}`) !== "" : true;
        const hasContactEmail = index !== undefined ? watch(`contactEmail-${index}`) !== "" : true;
        const hasShipmentAddress1 = index !== undefined ? watch(`shipmentAddress_line_1-${index}`) !== "" : true;
        const hasShipmentAddress2 = index !== undefined ? watch(`shipmentAddress_line_2-${index}`) !== "" : false;
        const hasShipmentPostalCode = index !== undefined ? watch(`shipmentPostalCode-${index}`) !== "" : true;
        const hasShipmentCity = index !== undefined ? watch(`shipmentCity-${index}`) !== "" : true;
        const hasShipmentState = index !== undefined ? watch(`shipmentState-${index}`) !== "" : true;
        const hasShipmentCountry = index !== undefined ? watch(`shipmentCountry-${index}`) !== "" : true;
        const hasShipmentIngestionSite = index !== undefined ? watch(`shipmentIngestionSite-${index}`) !== "" : true;

        /**
         * Renders a Textfield with label, name and options
         * @param {string} label - The label of the field
         * @param {string} mainFName - The main field name in case this is used to show an existing shipment values
         * @param {string} addFNameVariant - The field name variant in case this is used to add a new shipment
         * @param {object} options - The options for the field (like required and validation pattern)
         * @param {number} xsValue - The xs value for the grid item
         * @param {array | undefined} selectValuesArray - The values for the select field (if any)
         * @param {string | undefined} addFDefaultValue - The default value for the field in case this is used to add a new shipment
         * @returns {Element}
         */
        const renderField = (label, mainFName, addFNameVariant, options, xsValue, selectValuesArray = [], addFDefaultValue = undefined) => {
            const fieldName = index !== undefined ? mainFName : addFNameVariant;
            const defaultValue = index !== undefined ? watch(fieldName) : addFDefaultValue;
            const fieldProps = index === undefined ? registerNewShipment(fieldName, options) : {};
            const error = !!errorsNewShipment[fieldName];
            const errorMessage = errorsNewShipment[fieldName]?.message;


            return (
                <Grid item xs={xsValue} sx={flexStyles.flexColumnStart}>
                    <Typography align='left' className={classes.fieldLabel}>{label}</Typography>
                    <TextField
                        key={`${fieldName}:${defaultValue}`}
                        className={classes.selectLabelGreyDiff}
                        id="outlined-basic"
                        sx={{width: '100%'}}
                        variant="outlined"
                        name={fieldName}
                        select={Boolean(selectValuesArray?.length)}
                        defaultValue={defaultValue}
                        {...fieldProps}
                        error={error}
                    >
                        {Boolean(selectValuesArray?.length) && selectValuesArray.map((r) => (
                            <MenuItem key={r.key} value={r.key}>
                                {r.label}
                            </MenuItem>
                        ))}
                    </TextField>
                    <FormHelperText error>
                        {errorMessage}
                    </FormHelperText>
                </Grid>
            );
        };

        const renderRow1 = () => {
            if (index === undefined) {
                // New shipment — replace contact fields with UserPicker
                return (
                    <Grid item xs={12}>
                        <input type="hidden" {...registerNewShipment('newShipmentContactFirstName')}/>
                        <input type="hidden" {...registerNewShipment('newShipmentContactLastName')}/>
                        <input type="hidden" {...registerNewShipment('newShipmentContactPhoneNumber')}/>
                        <input type="hidden" {...registerNewShipment('newShipmentContactMobileNumber')}/>
                        <input type="hidden" {...registerNewShipment('newShipmentContactEmail')}/>
                        <UserPicker
                            label="Contact Person"
                            value={{
                                first_name:    watchNewShipment('newShipmentContactFirstName'),
                                last_name:     watchNewShipment('newShipmentContactLastName'),
                                phone_number:  watchNewShipment('newShipmentContactPhoneNumber'),
                                mobile_number: watchNewShipment('newShipmentContactMobileNumber'),
                                email:         watchNewShipment('newShipmentContactEmail'),
                            }}
                            onChange={(user) => {
                                setValueNewShipment('newShipmentContactFirstName',    user.first_name    ?? '');
                                setValueNewShipment('newShipmentContactLastName',     user.last_name     ?? '');
                                setValueNewShipment('newShipmentContactPhoneNumber',  user.phone_number  ?? '');
                                setValueNewShipment('newShipmentContactMobileNumber', user.mobile_number ?? '');
                                setValueNewShipment('newShipmentContactEmail',        user.email         ?? '');
                            }}
                            canToggle={true}
                            active={true}
                            existingUsers={customerUsers}
                            onRefresh={() => fetchAllCustomerContacts(customer?.uuid, { forceRefresh: true }).then(result => setCustomerUsers(result)).catch(() => {})}
                        />
                    </Grid>
                );
            }
            // Existing shipment — render contact as UserBadge
            const hasAnyContact = hasContactFirstName || hasContactLastName || hasContactPhoneNumber || hasContactMobileNumber || hasContactEmail;
            if (!hasAnyContact) return (<></>);
            const contactValue = {
                first_name:    watch(`contactFirstName-${index}`),
                last_name:     watch(`contactLastName-${index}`),
                phone_number:  watch(`contactPhoneNumber-${index}`),
                mobile_number: watch(`contactMobileNumber-${index}`),
                email:         watch(`contactEmail-${index}`),
            };
            const handleContactChange = (user) => {
                setValue(`contactFirstName-${index}`,    user.first_name    ?? "");
                setValue(`contactLastName-${index}`,     user.last_name     ?? "");
                setValue(`contactPhoneNumber-${index}`,  user.phone_number  ?? "");
                setValue(`contactMobileNumber-${index}`, user.mobile_number ?? "");
                setValue(`contactEmail-${index}`,        user.email         ?? "");
            };
            const isActive = isMinimumStage(0, index);
            return (
                <Grid item xs={12}>
                    <UserBadge
                        value={contactValue}
                        onChange={handleContactChange}
                        label="Contact"
                        canToggle={true}
                        active={isActive}
                    />
                </Grid>
            );
        };

        const renderRow2 = () => (<></>); // handled by UserPicker in renderRow1

        const renderRow3 = () => {
            if (index === undefined) {
                const existingAddresses = [
                    ...(customer?.contact_address?.address_line_1 ? [{...customer.contact_address, _label: customer.company_name}] : []),
                    ...(shipments || [])
                        .filter(s => s.shipping_address?.address_line_1)
                        .map(s => ({
                            address_uuid:   s.shipping_address.uuid,
                            address_line_1: s.shipping_address.address_line_1,
                            address_line_2: s.shipping_address.address_line_2,
                            postal_code:    s.shipping_address.postal_code,
                            city:           s.shipping_address.city,
                            state:          s.shipping_address.state,
                            country:        s.shipping_address.country,
                            _label:         customer?.company_name,
                        })),
                ];
                return (
                    <Grid item xs={12}>
                        <input type="hidden" {...registerNewShipment('newShipmentAddress_line_1')}/>
                        <input type="hidden" {...registerNewShipment('newShipmentAddress_line_2')}/>
                        <input type="hidden" {...registerNewShipment('newShipmentPostalCode')}/>
                        <input type="hidden" {...registerNewShipment('newShipmentCity')}/>
                        <input type="hidden" {...registerNewShipment('newShipmentState')}/>
                        <input type="hidden" {...registerNewShipment('newShipmentCountry')}/>
                        <AddressPicker
                            label="Shipping Address"
                            value={{
                                address_line_1: watchNewShipment('newShipmentAddress_line_1'),
                                address_line_2: watchNewShipment('newShipmentAddress_line_2'),
                                postal_code:    watchNewShipment('newShipmentPostalCode'),
                                city:           watchNewShipment('newShipmentCity'),
                                state:          watchNewShipment('newShipmentState'),
                                country:        watchNewShipment('newShipmentCountry'),
                            }}
                            onChange={(addr) => {
                                setValueNewShipment('newShipmentAddress_line_1', addr.address_line_1 ?? '');
                                setValueNewShipment('newShipmentAddress_line_2', addr.address_line_2 ?? '');
                                setValueNewShipment('newShipmentPostalCode',     addr.postal_code    ?? '');
                                setValueNewShipment('newShipmentCity',           addr.city           ?? '');
                                setValueNewShipment('newShipmentState',          addr.state          ?? '');
                                setValueNewShipment('newShipmentCountry',        addr.country        ?? '');
                            }}
                            canToggle={true}
                            active={true}
                            existingAddresses={existingAddresses}
                        />
                    </Grid>
                );
            }
            // Existing shipment — render all address fields as a single AddressBadge
            if (!hasShipmentAddress1 && !hasShipmentPostalCode && !hasShipmentCity && !hasShipmentState && !hasShipmentCountry) return (<></>);
            const addressValue = {
                address_line_1: watch(`shipmentAddress_line_1-${index}`),
                address_line_2: watch(`shipmentAddress_line_2-${index}`),
                postal_code:    watch(`shipmentPostalCode-${index}`),
                city:           watch(`shipmentCity-${index}`),
                state:          watch(`shipmentState-${index}`),
                country:        watch(`shipmentCountry-${index}`),
            };
            const handleAddressChange = (addr) => {
                setValue(`shipmentAddress_line_1-${index}`, addr.address_line_1 ?? "");
                setValue(`shipmentAddress_line_2-${index}`, addr.address_line_2 ?? "");
                setValue(`shipmentPostalCode-${index}`,     addr.postal_code    ?? "");
                setValue(`shipmentCity-${index}`,           addr.city           ?? "");
                setValue(`shipmentState-${index}`,          addr.state          ?? "");
                setValue(`shipmentCountry-${index}`,        addr.country        ?? "");
            };
            const isActive = isMinimumStage(0, index);
            return (
                <Grid item xs={12}>
                    <AddressBadge
                        value={addressValue}
                        onChange={handleAddressChange}
                        label="Shipping Address"
                        canToggle={true}
                        active={isActive}
                    />
                </Grid>
            );
        };

        const renderRow4 = () => (<></>); // handled by AddressPicker in renderRow3

        const renderRow5 = () => (<></>); // handled by AddressPicker in renderRow3

        const renderRow6 = () => {
            if (!hasShipmentIngestionSite) return (<></>)
            return renderField("Shipment Ingestion Site", `shipmentIngestionSite-${index}`, `newShipmentIngestionSite`, ingestionSiteValidationRules, 12, INGESTION_SITES, "perth");
        };

        if (!hasContactFirstName && !hasContactLastName && !hasContactMobileNumber && !hasContactPhoneNumber && !hasContactEmail && !hasShipmentAddress1 && !hasShipmentAddress2 && !hasShipmentPostalCode && !hasShipmentCity && !hasShipmentState && !hasShipmentCountry && !hasShipmentIngestionSite) return (<></>)

        // 2 Grid items one below the other. One for the title and the other one holding a parent column. Content = greyed out
        return (
            <>
                {/*This column grid will contain all the child Grid items (each row)*/}
                {renderRow1()}
                {renderRow2()}
                {renderRow3()}
                {renderRow4()}
                {renderRow5()}
                {renderRow6()}
            </>
        );
    };

    /**
     * Renders the initial details of the shipment or empty fields for adding a new shipment
     * @param {number | undefined} index - The shipment index or undefined for new shipment to add
     * @returns {Element}
     */

    const renderInitialDetails = (index = undefined) => {
        if (!isDefaultLoaded) return (<></>)

        return (<>
            <Grid item>
                <Typography align='left' sx={{
                    ...classes.textWhite,
                }}>Details</Typography>
            </Grid>
            <Grid item xs={12} sx={{...(index !== undefined && {opacity: 0.5, pointerEvents: 'none'})}}>
                <Grid container direction='column' spacing={2}>
                    {order.shipping_mode === 'tapeark' && renderTapeArkInitialDetails(index)}
                    {order.shipping_mode === 'self' && renderSelfPickupInitialDetails(index)}
                </Grid>
            </Grid>
        </>);


    };
    const handleDeleteShipment = (currentIndex) => {
        deleteShipment(shipments[currentIndex]?.order, shipments[currentIndex]?.uuid, {customerId: customer?.uuid})
            .then(response => {
                refreshShipmentDetails();
                setShipmentTab(0);
            })
            .catch(err => console.log(err));
    };

    const renderShippingStatus = (index) => {
        if (!isDefaultLoaded) return (<></>)

        const current = watch(`shipmentStatus-${index}`);
        const currentStageIndex = STATUS_SEQUENCE.findIndex(s => s.value === current);

        const deleteShipmentButton = () => {
            return (
                <Tooltip title="Delete Shipment">
                    <StyledButtonOutlined
                        variant="outlined"
                        onClick={() => handleDeleteShipment(index)}
                        sx={{height: '40px', width: '40px', minWidth: '40px', borderRadius: '8px', padding: 0}}
                    >
                        <DeleteOutline sx={{color: '#FFC540'}}/>
                    </StyledButtonOutlined>
                </Tooltip>
            );
        };

        // crash early if somehow the value isn't in STAGES
        if (currentStageIndex < 0) {
            return (
                <Grid item sx={flexStyles.flexRowStart} alignItems="center" gap={2}>
                    {deleteShipmentButton()}
                    <Typography
                        align="left"
                        sx={{...classes.textWhite}}
                    >{`Invalid shipment status "${current}" for shipmnent ${index}`}</Typography>
                </Grid>);
        }

        const stageLabel = STATUS_SEQUENCE[currentStageIndex].label;
        const isLast = currentStageIndex === STATUS_SEQUENCE.length - 1;

        return (
            <Grid item sx={flexStyles.flexRowStart} alignItems="center" gap={2}>
                {deleteShipmentButton()}
                <Typography
                    align="left"
                    sx={{...classes.textWhite}}
                >Shipping Status</Typography>

                <TextField
                    disabled
                    fullWidth
                    className={classes.selectLabelGreyDiff}
                    name={`shipmentStatus-${index}`}
                    value={stageLabel}
                />

                {!isLast && (
                    <Typography
                        align="left"
                        sx={{color: '#FFC540', fontFamily: 'Poppins', fontSize: '14px'}}
                    >Saving will advance to next stage</Typography>
                )}
            </Grid>
        );
    };

    /**
     * Renders the Self Pickup Initial Details of the shipment or empty fields for adding a new shipment
     * @param {number | undefined} index - The shipment index or undefined for new shipment to add
     * @returns {Element}
     */

    const renderSelfPickupInitialDetails = (index = undefined) => {
        const fieldName1 = index !== undefined ? `selfShipmentTrackingLink-${index}` : `newSelfShipmentTrackingLink`;
        const defaultValue1 = index !== undefined ? watch(fieldName1) : undefined;

        const fieldProps1 = index === undefined ? registerNewShipment(fieldName1, trackingLinkValidationRules) : {};
        const error1 = !!errorsNewShipment[fieldName1];
        const errorMessage1 = errorsNewShipment[fieldName1]?.message;

        return (
            <Grid item xs={12} sx={flexStyles.flexColumnStart}>
                <Typography align='left' className={classes.fieldLabel} sx={{opacity: 0.5}}>Tracking Link</Typography>
                <TextField className={classes.selectLabelGreyDiff} id="outlined-basic" sx={{width: '100%'}}
                           variant="outlined"
                           name={fieldName1}
                           defaultValue={defaultValue1}
                           {...fieldProps1}
                           error={error1}
                />
                <FormHelperText error>
                    {errorMessage1}
                </FormHelperText>
            </Grid>
        );
    };

    /**
     * Renders the Tape Ark Pickup Information of the shipment
     * @param index - The shipment index
     * @param thisStage - The current stage of the shipment
     * @returns {Element}
     */
    const renderTapeArkPickupInformation = (index, thisStage) => {
        return (
            <>
                <Stack direction={'column'}>
                    <Typography className={classes.fieldLabel} align='left'>Consignment Number</Typography>
                    <TextField className={classes.selectLabelGreyDiff}
                               variant="outlined" sx={{width: '100%'}}
                               name={`pickupConsignmentNumber-${index}`}
                               {...register(
                                   `pickupConsignmentNumber-${index}`,
                                   {
                                       ...(isMinimumStage(thisStage, index) && consignmentNumberValidationRules
                                       )
                                   }
                               )}
                               error={!!errors[`pickupConsignmentNumber-${index}`]}
                    />
                    <FormHelperText error>
                        {errors[`pickupConsignmentNumber-${index}`]?.message}
                    </FormHelperText>

                </Stack>
                <Stack direction={'column'}>
                    <Typography className={classes.fieldLabel} align='left'>Contact Number</Typography>
                    <TextField className={classes.selectLabelGreyDiff}
                               variant="outlined" sx={{width: '100%'}}
                               name={`pickupConsignmentContact-${index}`}
                               {...register(
                                   `pickupConsignmentContact-${index}`,
                                   {
                                       ...(isMinimumStage(thisStage, index) && phoneNumberValidationRules) // the below validation active only if this stage is active
                                   }
                               )}
                               error={!!errors[`pickupConsignmentContact-${index}`]}
                    />
                    <FormHelperText error>
                        {errors[`pickupConsignmentContact-${index}`]?.message}
                    </FormHelperText>
                </Stack>
            </>
        );
    };

    const renderPickupInformation = (index) => {
        const thisStage = 1;

        return (
            <>
                <Grid item>
                    <Typography align='left' sx={{
                        ...classes.textWhite,
                        ...(!isMinimumStage(thisStage, index) && {opacity: 0.5,}),
                    }}>Pickup Information</Typography>
                </Grid>
                <Grid item xs={12} sx={{
                    ...flexStyles.flexRowStart,
                    gap: 8,
                    ...(!isMinimumStage(thisStage, index) && {opacity: 0.5, pointerEvents: 'none'}),
                }}>
                    {renderTapeArkPickupInformation(index, thisStage)}
                </Grid>
            </>
        );
    };

    const isMinimumStage = (stage, index) => {
        return getShipmentStage(watch(`shipmentStatus-${index}`)) >= stage;
    };

    const sectionSx = (sectionStage, index) => {
        const currentStage = getShipmentStage(watch(`shipmentStatus-${index}`));
        if (sectionStage === currentStage) return {borderLeft: '5px solid #FFC540', pl: 2, ml: -1};
        if (sectionStage > currentStage) return {opacity: 0.35, pointerEvents: 'none'};
        return {};
    };

    const renderConfirmReceipt = (index) => {
        const thisStage = 2;

        return (
            <>
                <Grid
                    item
                    sx={{...(!isMinimumStage(thisStage, index) && {opacity: 0.5})}}>
                    <Typography align='left' sx={{
                        ...classes.textWhite,
                    }}>Confirm Receipt</Typography>
                </Grid>
                <Grid sx={{
                    ...(!isMinimumStage(thisStage, index) && {opacity: 0.5, pointerEvents: 'none'}),
                    ...flexStyles.flexColumnStart
                }} item xs={12} gap={2}>

                    <Typography className={classes.fieldLabel} align='left'>Number Of Boxes</Typography>
                    <TextField className={classes.selectLabelGreyDiff} id="outlined-basic"
                               type="number"
                               inputProps={{
                                   step: "1",
                                   min: 0,
                                   max: 99999999
                               }}
                               variant="outlined" fullWidth
                               name={`pickupNumberOfBoxes-${index}`}
                               {...register(`pickupNumberOfBoxes-${index}`)} />

                    <Typography className={classes.fieldLabel} align='left'>Weight</Typography>
                    <TextField className={classes.selectLabelGreyDiff} id="outlined-basic"
                               variant="outlined" fullWidth={true}
                               name={`pickupWeight-${index}`}
                               type="number"                        // native number input
                               inputProps={{
                                   step: "0.01",                       // two‐decimal precision
                                   min: 0,
                                   max: 99999999.99,
                                   inputMode: "decimal",               // mobile decimal keyboard
                               }}
                               {...register(
                                   `pickupWeight-${index}`,
                                   {
                                       ...(isMinimumStage(thisStage, index) && twoDecimalPrecisionNumberValidationRules) // the below validation active only if this stage is active
                                   }
                               )}
                               error={!!errors[`pickupWeight-${index}`]}
                    />
                    <FormHelperText error>
                        {errors[`pickupWeight-${index}`]?.message}
                    </FormHelperText>

                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <Stack direction="row" gap={2}>
                            <Box>
                                <Typography className={classes.fieldLabel} align='left'>Date Of Arrival</Typography>

                                <DatePicker
                                    onChange={(newValue) => setValue(`pickupDateOfArrival-${index}`, newValue.toISOString())}
                                    defaultValue={(watch(`pickupDateOfArrival-${index}`)) ? dayjs(watch(`pickupDateOfArrival-${index}`)) : undefined}
                                    className='date-input'
                                    sx={{width: '100%'}}
                                />
                            </Box>
                            <Box>

                                <Typography className={classes.fieldLabel} align='left'>Time Of Arrival</Typography>

                                <TimePicker
                                    onChange={(newValue) => setValue(`pickupTime-${index}`, newValue.toISOString())}
                                    defaultValue={(watch(`pickupTime-${index}`)) ? dayjs(watch(`pickupTime-${index}`)) : undefined}
                                    className='date-input'
                                    sx={{width: '100%'}}
                                    ampm={false}
                                />
                            </Box>
                        </Stack>

                    </LocalizationProvider>
                </Grid>
            </>
        );
    };

    const renderDeliveryInformation = (index) => {
        const thisStage = 3;
        return (
            <>
                <Grid item sx={{
                    ...(!isMinimumStage(thisStage, index) && {opacity: 0.5, pointerEvents: 'none'}),
                }}>
                    <Typography align='left' sx={{
                        ...classes.textWhite,
                    }}>Delivery Information</Typography>
                </Grid>
                <Grid item xs={12} sx={{
                    ...(!isMinimumStage(thisStage, index) && {opacity: 0.5, pointerEvents: 'none'}),
                    ...flexStyles.flexRowStart,
                    gap: 8,
                }}>
                    <Stack direction={'column'}>
                        <Typography className={classes.fieldLabel} align='left'>Consignment Number</Typography>
                        <TextField className={classes.selectLabelGreyDiff}
                                   variant="outlined" sx={{width: '100%'}}
                                   name={`deliveryConsignmentNumber-${index}`}
                                   {...register(
                                       `deliveryConsignmentNumber-${index}`,
                                       {
                                           ...(isMinimumStage(thisStage, index) && consignmentNumberValidationRules)
                                       }
                                   )}
                                   error={!!errors[`deliveryConsignmentNumber-${index}`]}
                        />
                        <FormHelperText error>
                            {errors[`deliveryConsignmentNumber-${index}`]?.message}
                        </FormHelperText>

                    </Stack>
                    <Stack direction={'column'}>
                        <Typography className={classes.fieldLabel} align='left'>Contact Number</Typography>
                        <TextField className={classes.selectLabelGreyDiff}
                                   variant="outlined" sx={{width: '100%'}}
                                   name={`deliveryContact-${index}`}
                                   {...register(`deliveryContact-${index}`,
                                       {
                                           ...(isMinimumStage(thisStage, index) && phoneNumberValidationRules)// the below validation active only if this stage is active
                                       }
                                   )}
                                   error={!!errors[`deliveryContact-${index}`]}
                        />
                        <FormHelperText error>
                            {errors[`deliveryContact-${index}`]?.message}
                        </FormHelperText>
                    </Stack>

                </Grid>
            </>
        );
    };

    const renderShippingDetails = (shipment, index) => {
        const thisStage = 4;

        return (<>
            <Grid item>
                <Typography align='left' sx={{
                    ...classes.textWhite,
                    ...(!isMinimumStage(thisStage, index) && {opacity: 0.5,}),

                }}>Shipping Details</Typography>
            </Grid>
            <Grid item xs={12} sx={{
                ...(!isMinimumStage(thisStage, index) && {opacity: 0.5, pointerEvents: 'none'}),

            }}>
                <Grid container direction='column' spacing={2}>
                    <Grid item>
                        <Grid container direction='row' justifyContent='flex-start' spacing={2} alignItems={'flex-start'}>
                            {shipments && (
                                <Grid item xs={5} sx={flexStyles.flexColumnStart}>
                                    <Typography align='left' className={classes.fieldLabel}>Carrier Name</Typography>
                                    <TextField
                                        className={classes.selectLabelGreyDiff}
                                        sx={{width: '100%'}}
                                        name={`shipmentCarrier-${index}`}
                                        select
                                        defaultValue={watch(`shipmentCarrier-${index}`) ? watch(`shipmentCarrier-${index}`) : ""}
                                        {...register(`shipmentCarrier-${index}`)}>
                                        <MenuItem value={'fedex'}>FedEx</MenuItem>
                                        <MenuItem value={'ups'}>UPS</MenuItem>
                                        <MenuItem value={'mnx'}>MNS</MenuItem>
                                        <MenuItem value={'dhl'}>DHL</MenuItem>
                                    </TextField>
                                </Grid>
                            )}
                            <Grid item xs={4} sx={flexStyles.flexColumnStart}>
                                <Typography align='left' className={classes.fieldLabel}>Consignment Number</Typography>
                                <TextField className={classes.selectLabelGreyDiff} id="outlined-basic" sx={{width: '100%'}} variant="outlined"
                                           name={`shipmentConsignmentNumber-${index}`}
                                           {...register(
                                               `shipmentConsignmentNumber-${index}`,
                                               {
                                                   ...(isMinimumStage(thisStage, index) && consignmentNumberValidationRules)
                                               }
                                           )}
                                           error={!!errors[`shipmentConsignmentNumber-${index}`]}
                                />
                                <FormHelperText error>
                                    {errors[`shipmentConsignmentNumber-${index}`]?.message}
                                </FormHelperText>
                            </Grid>
                            <Grid item xs={3} sx={flexStyles.flexColumnStart}>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <Typography className={classes.fieldLabel} align='left'>Received On</Typography>
                                    <DatePicker
                                        onChange={(newValue) => setValue(`shippingReceivedOn-${index}`, newValue.toISOString())}
                                        defaultValue={(watch(`shippingReceivedOn-${index}`)) ? dayjs(watch(`shippingReceivedOn-${index}`)) : undefined}
                                        className='date-input'
                                        sx={{width: '100%'}}
                                    />
                                </LocalizationProvider>
                            </Grid>

                        </Grid>
                    </Grid>
                    <Grid item>
                        <Grid container direction='row' justifyContent='flex-start' spacing={2}>
                            <Grid item xs={3} sx={flexStyles.flexColumnStart}>
                                <Typography align='left' className={classes.fieldLabel}>Type Of Shipment</Typography>
                                <TextField
                                    select
                                    className={classes.selectLabelGreyDiff}
                                    sx={{width: '100%'}}
                                    name={`shipmentPackaging-${index}`}
                                    defaultValue={watch(`shipmentPackaging-${index}`) ? watch(`shipmentPackaging-${index}`) : "boxes"}
                                    {...register(`shipmentPackaging-${index}`)} >
                                    <MenuItem key={'boxes'} value={'boxes'}>Boxes</MenuItem>
                                </TextField>
                            </Grid>
                            <Grid item xs={3} sx={flexStyles.flexColumnStart}>
                                <Typography align='left' className={classes.fieldLabel}>Number Of Boxes</Typography>
                                <TextField
                                    type="number"
                                    inputProps={{
                                        step: "1",
                                        min: 0,
                                        max: 99999999
                                    }}
                                    className={classes.selectLabelGreyDiff}
                                    sx={{width: '100%'}}
                                    name={`shipmentNoOfBoxes-${index}`}
                                    {...register(`shipmentNoOfBoxes-${index}`)}
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item>
                        <Grid container direction='row' justifyContent='flex-start' spacing={2}>
                            <Grid item xs={5} sx={flexStyles.flexColumnStart}>
                                <Typography align='left' className={classes.fieldLabel}>Description/Message (If any)</Typography>
                                <TextField multiline rows={3} sx={{width: '100%'}} className={classes.selectLabelGreyDiff} id="outlined-basic" variant="outlined"
                                           name={`shipmentDescription-${index}`}
                                           defaultValue={watch(`shipmentDescription-${index}`) ? watch(`shipmentDescription-${index}`) : ""}
                                           {...register(
                                               `shipmentDescription-${index}`,
                                               {
                                                   ...(isMinimumStage(thisStage, index) && descriptionMessageValidationRules(100))
                                               }
                                           )} />
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item md={12} mb={2} sx={flexStyles.flexRowStart}>
                        <Divider sx={{background: '#5F5F5F', width: '100%'}} orientation='horizontal'/>
                    </Grid>
                    <Grid item>
                        <Typography align='left' sx={{
                            ...(!isMinimumStage(thisStage, index) && {opacity: 0.5,}),
                            ...classes.textWhite,
                        }}>Verification</Typography>
                    </Grid>
                    <Grid item xs={5} sx={flexStyles.flexColumnStart}>
                        <Typography align='left' className={classes.fieldLabel}>Total Expected Tapes</Typography>
                        <TextField sx={{width: '30%'}} className={classes.selectLabelGreyDiff} id="outlined-basic" variant="outlined"
                                   name={`verificationTotalTapes-${index}`}
                                   {...register(`verificationTotalTapes-${index}`)} />
                        {expectedMessage.message && (
                            <Typography
                                align='left'
                                className={classes.fieldLabel}
                                sx={{
                                    color: theme => expectedMessage.error
                                        ? theme.palette.error.main
                                        : '#FFC540'
                                }}
                            >{expectedMessage.message}
                            </Typography>
                        )}
                    </Grid>
                    <ShipmentVerifications
                        shipmentIndex={index}
                        shipment={shipment}
                        control={control}
                        watch={watch}
                        register={register}
                        setValue={setValue}
                        orderId={orderId}
                        totalExpectedTapes={watch(`verificationTotalTapes-${index}`)}
                        setExpectedMessage={setExpectedMessage}
                    />

                </Grid>
            </Grid>
        </>);

    };

    const renderShipmentTabContent = (shipment, index) => {
        if (index === shipmentTab && shipmentTab > (shipments.length - 1)) return (<></>)

        return (
            <TabPanel key={index} value={shipmentTab} index={index}>
                <form id={index} onSubmit={handleSubmit(onSubmit, errors => {
                    console.log("Validation errors:", errors);
                })}>
                    <Grid container direction='column' spacing={2}>
                        {renderShippingStatus(index)}
                        <Grid item md={12} mb={2} sx={flexStyles.flexRowStart}>
                            <Divider sx={{background: '#5F5F5F', width: '100%'}} orientation='horizontal'/>
                        </Grid>
                        <Box sx={sectionSx(0, index)}>
                            {renderInitialDetails(index)}
                        </Box>
                        <Grid item md={12} mb={2} sx={flexStyles.flexRowStart}>
                            <Divider sx={{background: '#5F5F5F', width: '100%'}} orientation='horizontal'/>
                        </Grid>
                        {order.shipping_mode === 'tapeark' && (
                            <>
                                <Box sx={sectionSx(1, index)}>
                                    {renderPickupInformation(index)}
                                </Box>
                                <Grid item md={12} mb={2} sx={flexStyles.flexRowStart}>
                                    <Divider sx={{background: '#5F5F5F', width: '100%'}} orientation='horizontal'/>
                                </Grid>
                            </>
                        )}
                        <Box sx={sectionSx(2, index)}>
                            {renderConfirmReceipt(index)}
                        </Box>
                        <Grid item md={12} mb={2} sx={flexStyles.flexRowStart}>
                            <Divider sx={{background: '#5F5F5F', width: '100%'}} orientation='horizontal'/>
                        </Grid>
                        <Box sx={sectionSx(3, index)}>
                            {renderDeliveryInformation(index)}
                        </Box>
                        <Grid item md={12} mb={2} sx={flexStyles.flexRowStart}>
                            <Divider sx={{background: '#5F5F5F', width: '100%'}} orientation='horizontal'/>
                        </Grid>
                        <Box sx={sectionSx(4, index)}>
                            {renderShippingDetails(shipment, index)}
                        </Box>
                    </Grid>

                    {/* Manage Shipment Action Drawer */}
                    <Drawer variant={'persistent'} anchor={'bottom'} open sx={{
                        '& .MuiDrawer-paper': {
                            backgroundColor: '#2C2C2C',
                            boxShadow: '0px -7px 11px 0px #00000029',
                            height: '6rem',
                            pl: '60px', // TODO: Make width of Navbar Drawer and move it to constants
                        },
                    }}>
                        <Grid container direction={'row'} my={2} px={8}>
                            <Grid item xs={6} sx={flexStyles.flexRowStart}>
                                <Typography className={classes.valueWhite} sx={{textAlign: 'left', mt: 2}}>
                                    {getStatusSectionText(watch(`shipmentStatus-${index}`))}</Typography>
                            </Grid>

                            <Grid item xs={6} sx={flexStyles.flexRowEnd} gap={2}>

                                <StyledButtonOutlined
                                    variant="outlined"
                                    onClick={() => initializeForm()}
                                    sx={{width: '220px', height: '60px'}}>
                                    Cancel
                                </StyledButtonOutlined>

                                <StyledButtonContained variant='contained'
                                                       type='submit'
                                    //form="mainForm"
                                                       sx={{
                                                           width: '220px',
                                                           height: '60px'
                                                       }}>
                                    Save
                                </StyledButtonContained>

                            </Grid>
                        </Grid>
                    </Drawer>
                </form>
            </TabPanel>
        );
    };

    const submitNewShipment = () => {
        if (!['tapeark', 'self'].includes(order.shipping_mode)) throw new Error(`Invalid shipping mode for adding new shipment: ${order.shipping_mode}`);

        console.log("New shipment addition - form data:", getValuesNewShipment());
        console.log("New shipment addition other details", "shipments", shipments, "customer", customer, "order", order);

        const newShipmentData = getValuesNewShipment();
        let data = {};

        if (order.shipping_mode === 'tapeark') {
            data = {
                first_name: newShipmentData?.newShipmentContactFirstName,
                last_name: newShipmentData?.newShipmentContactLastName,
                phone_number: newShipmentData?.newShipmentContactPhoneNumber,
                mobile_number: newShipmentData?.newShipmentContactMobileNumber,
                optional_email: newShipmentData?.newShipmentContactEmail,
                ingestion_site: newShipmentData?.newShipmentIngestionSite,
                order: order.uuid,
                shipping_address:
                    {
                        address_line_1: newShipmentData?.newShipmentAddress_line_1,
                        postal_code: newShipmentData?.newShipmentPostalCode,
                        city: newShipmentData?.newShipmentCity,
                        state: newShipmentData?.newShipmentState,
                        country: newShipmentData?.newShipmentCountry
                    }
            };
        } else {
            data = {
                tracking_link: newShipmentData?.newSelfShipmentTrackingLink,
                order: order.uuid,
            };
        }

        console.log("New shipment addition - data to be sent:", data);
        addShipment(data, {customerId: customer?.uuid}).then((response) => {
            console.log("add new shipment response ->", response);
            closeNewShipmentAddForm();
            refreshShipmentDetails();
        }).catch(err => console.log("submitNewShipment error ->", err));
    };

    const renderNewShipmentContent = () => {
        return (
            <>
                <form id="newShipmentForm" onSubmit={handleSubmitNewShipment(submitNewShipment,
                    errors => {
                        console.log("New shipment addition - validation errors:", errors);
                    })
                }>
                    <Grid container direction='column' spacing={2}>
                        {renderInitialDetails()}
                    </Grid>

                    {/* Manage Shipment Action Drawer */}
                    <Drawer variant={'persistent'} anchor={'bottom'} open sx={{
                        '& .MuiDrawer-paper': {
                            backgroundColor: '#2C2C2C',
                            boxShadow: '0px -7px 11px 0px #00000029',
                            height: '6rem',
                            pl: '60px', // TODO: Make width of Navbar Drawer and move it to constants
                        },
                    }}>
                        <Grid container direction={'row'} my={2} px={8}>
                            <Grid item xs={6} sx={flexStyles.flexRowStart}>
                                <Typography sx={{...classes.textWhite, textAlign: 'left', fontSize: 18, fontWeight: 400, mt: 2}}>
                                    New shipment details</Typography>
                            </Grid>

                            <Grid item xs={6} sx={flexStyles.flexRowEnd} gap={2}>
                                <StyledButtonOutlined
                                    variant="outlined"
                                    onClick={() => closeNewShipmentAddForm()}
                                    sx={{width: '220px', height: '60px'}}>
                                    Cancel
                                </StyledButtonOutlined>

                                <StyledButtonContained variant='contained'
                                                       type='submit'
                                                       sx={{
                                                           width: '220px',
                                                           height: '60px'
                                                       }}>
                                    Add shipment
                                </StyledButtonContained>

                            </Grid>
                        </Grid>
                    </Drawer>
                </form>
            </>
        );
    };

    return (
        <Box p={4}>
            {isDefaultLoaded ? (<>
                    <Grid container direction='column' spacing={2}>
                        {order.shipping_mode && (<>
                            <Grid item xs={12} sx={flexStyles.flexRowStart} alignItems={'center'} mb={2}>
                                <Typography align='left' sx={{
                                    ...classes.textWhite, whiteSpace: 'pre',
                                }}>Shipping Mode: </Typography>
                                <Typography sx={{
                                    ...classes.textWhite, fontSize: '22px',
                                }}>{mapShippingModeText(order.shipping_mode)}</Typography>
                            </Grid>
                            <Grid item xs={12} sx={{border: '1px solid #FFFFFF', visibility: (order.shipping_mode === 'none' ? 'hidden' : 'visible')}} p={2}>
                                <Grid container alignItems="center">
                                    <Grid item xs={11}>
                                        <CustomHTabList
                                            value={!newShipmentEnabled ? shipmentTab : -1}
                                            orientation='horizontal'
                                            onChange={handleShipmentTabChange}
                                            sx={{
                                                background: 'transparent',
                                                mb: 2,
                                            }}
                                        >
                                            {shipments?.length ?
                                                shipments?.map((shipment, index) => (
                                                    <CustomHTab value={index} key={`CustomHTab-${index}`} label={`Shipment${index + 1}`}/>))
                                                : <Typography
                                                    align="left"
                                                    sx={{
                                                        ...classes.textWhite,
                                                        opacity: 0.5
                                                    }}
                                                >No Shipment attached to this order</Typography>
                                            }
                                            {newShipmentEnabled &&
                                                <CustomHTab value={-1} key={`CustomHTab-${-1}`} label={`New Shipment`}/>
                                            }
                                        </CustomHTabList>
                                    </Grid>
                                    <Grid item xs={1} display="flex" justifyContent="flex-end" alignItems="center">
                                        <Tooltip title="Add Shipment">
                                            <StyledButtonOutlined
                                                variant="outlined"
                                                onClick={() => setNewShipmentEnabled(true)}
                                                sx={{height: '40px', width: '40px', minWidth: '40px', borderRadius: '8px', padding: 0}}
                                            >
                                                <Add sx={{color: '#FFC540'}}/>
                                            </StyledButtonOutlined>
                                        </Tooltip>
                                    </Grid>
                                </Grid>
                            </Grid>
                            {order.shipping_mode === "none" && (
                                renderExternalPickupInformation()
                            )}
                        </>)}
                        <Grid item xs={12}>
                            {!newShipmentEnabled ?
                                shipments?.map((shipment, index) => renderShipmentTabContent(shipment, index))
                                : renderNewShipmentContent()}
                        </Grid>

                    </Grid>
                    <Snackbar
                        anchorOrigin={{vertical, horizontal}}
                        open={openSb}
                        onClose={closeSnackbar}
                        message={snackbarMessage}
                        key={vertical + horizontal}
                    /></>
            ) : (
                <CircularProgress sx={{color: '#FFC540'}}></CircularProgress>
            )}
        </Box>
    )
}


const ShipmentVerifications = ({shipmentIndex, shipment, control, watch, register, setValue, orderId, totalExpectedTapes, setExpectedMessage}) => {

    const classes = useStyles();

    let arrayName = `verifications-${shipment?.uuid}`;

    const {
        fields: verificationFields,
        append: verificationAppend,
        prepend,
        remove: verificationRemove,
        swap, move, insert
    } = useFieldArray({
        control: control,
        name: arrayName
    });
    const verifications = useWatch({
        control,
        name: arrayName
    })

    const debounceTimeout = 800;

    const computeVerificationMatchMessage = useCallback((verifications, totalExpectedTapes) => {
        let sum = 0;
        verifications.forEach((verification, index) => {
            const n = parseInt(verification?.tape_count, 10);
            sum += Number.isNaN(n) ? 0 : n;
        });

        const n = parseInt(totalExpectedTapes, 10);
        const expectedN = Number.isNaN(n) ? 0 : n;

        const caseMap = {
            numbersEqual: {error: false, message: "Verifications match expected"},
            expectedGreater: {error: true, message: `Verifications lower than expected; Expected ${expectedN} tapes but got ${sum} during verification`},
            expectedLess: {error: true, message: `Verifications higher than expected. Expected ${expectedN} tapes but got ${sum} during verification`},
        };

        if (sum === expectedN) setExpectedMessage(caseMap.numbersEqual)
        else if (expectedN > sum) setExpectedMessage(caseMap.expectedGreater)
        else setExpectedMessage(caseMap.expectedLess)
    }, []);

    useEffect(() => {
        if (verifications === undefined) return;
        const handle = setTimeout(() => {
            computeVerificationMatchMessage(verifications, totalExpectedTapes);
        }, debounceTimeout);
        return () => clearTimeout(handle);
    }, [verifications, computeVerificationMatchMessage, totalExpectedTapes]);

    const [shipmentVerifications, setVerifications] = useState({});
    const [verificationsLoaded, setVerificationsLoaded] = useState(false);

    const populateVerifications = (shipment) => {
        fetchVerificationsDetails(orderId, shipment?.uuid)
            .then(response => {
                shipmentVerifications[shipment?.uuid] = response.results;
                if (shipmentVerifications[shipment?.uuid]?.length > 0) {
                    shipmentVerifications[shipment?.uuid].map((resV, index) => {
                        if (!watch(arrayName).some((v) => v?.uuid === resV?.uuid)) {
                            verificationAppend({
                                box_number: resV.box_number,
                                tape_count: resV.tape_count ?? '',
                                uuid: resV.uuid,
                                comments: resV.comments ? resV.comments : '',
                                has_transmittal: Boolean(resV.has_transmittal)
                            });
                        }
                    })
                } else if (watch(arrayName)?.length === 0) {
                    verificationAppend({box_number: '', tape_count: '', uuid: null, comments: '', has_transmittal: false});
                }
                setVerificationsLoaded(true);

            })
            .catch(err => {
                console.log(err);
                setVerificationsLoaded(true);
            });
    }

    useEffect(() => {
        if (shipment) {
            populateVerifications(shipment);
        }
    }, []);

    const deleteShipmentVerification = (index) => {
        const emptyFieldValues = {
            box_number: '',
            tape_count: '',
            comments: '',
            has_transmittal: false,
            uuid: null
        };

        const uuid = watch(`${arrayName}[${index}].uuid`);
        if (uuid) {
            deleteVerification(orderId, shipment?.uuid, uuid)
                .then(response => {
                    if (verificationFields.length === 1) setValue(`${arrayName}[0]`, emptyFieldValues)
                    else if (verificationFields.length > 1) verificationRemove(index)
                })
                .catch(err => console.log("deleteShipmentVerification ->", err));
        } else {
            if (verificationFields.length === 1) setValue(`${arrayName}[0]`, emptyFieldValues)
            else if (verificationFields.length > 1) verificationRemove(index)
        }
    }


    return (
        <>  {verificationsLoaded ?
            (
                <Grid item gap={2} sx={{
                    ...flexStyles.flexColumnStart,
                    ...((getShipmentStage(watch(`shipmentStatus-${shipmentIndex}`)) < 4) && {opacity: 0.5, pointerEvents: 'none'}),
                }}>
                    {verificationFields.map((field, j) => (
                        <Grid key={field.id} container direction='row' justifyContent='flex-start' spacing={2} mb={1}>
                            <Grid item>
                                <Typography align='left' className={classes.fieldLabel}>Box Number</Typography>
                                <FormControl fullWidth={true}>
                                    <TextField
                                        {...register(`${arrayName}[${j}].box_number`)}
                                        name={`${arrayName}[${j}].box_number`}
                                        className={classes.selectLabelGreyDiff}
                                        defaultValue={field.box_number}
                                        sx={{width: '15ch',}}
                                    />
                                </FormControl>
                            </Grid>
                            <Grid item>
                                <Typography align='left' className={classes.fieldLabel}>Tape Count</Typography>
                                <TextField
                                    {...register(`${arrayName}[${j}].tape_count`)}
                                    name={`${arrayName}[${j}].tape_count`}
                                    defaultValue={field.tape_count ?? ''}
                                    className={classes.selectLabelGreyDiff} id="outlined-basic"
                                    variant="outlined"
                                    type="number"
                                    inputProps={{
                                        step: 1,      // only allow integer stepping
                                        min: 0,       // or whatever your minimum is
                                    }}
                                    sx={{width: '15ch',}}/>
                            </Grid>
                            <Grid item>
                                <Typography align='left' className={classes.fieldLabel}>Comments</Typography>
                                <TextField
                                    {...register(`${arrayName}[${j}].comments`)}
                                    name={`${arrayName}[${j}].comments`}
                                    defaultValue={field.comments}
                                    className={classes.selectLabelGreyDiff} id="outlined-basic"
                                    variant="outlined"
                                    sx={{width: '30ch',}}/>
                            </Grid>
                            <Grid item>
                                <Typography align='left' className={classes.fieldLabel}>Has Transmittal</Typography>
                                <Switch
                                    key={field.uuid}
                                    {...register(`${arrayName}[${j}].has_transmittal`
                                    )}
                                    defaultChecked={field.has_transmittal}
                                    className={classes.switchLabelWhite}
                                />
                            </Grid>
                            <Grid item>
                                <Box mt={3}>
                                    <Tooltip title="Delete row">
                                        <StyledButtonOutlined
                                            variant="outlined"
                                            onClick={() => deleteShipmentVerification(j)}
                                            sx={{height: '40px', width: '40px', minWidth: '40px', borderRadius: '8px', padding: 0}}
                                        >
                                            <DeleteOutline sx={{color: '#FFC540'}}/>
                                        </StyledButtonOutlined>
                                    </Tooltip>
                                </Box>
                            </Grid>
                            <Grid item>
                                <Box mt={3}>
                                    <Tooltip title="Add row">
                                        <StyledButtonOutlined
                                            variant="outlined"
                                            onClick={() => verificationAppend({box_number: '', tape_count: '', comments: '', has_transmittal: false})}
                                            sx={{height: '40px', width: '40px', minWidth: '40px', borderRadius: '8px', padding: 0}}
                                        >
                                            <Add sx={{color: '#FFC540'}}/>
                                        </StyledButtonOutlined>
                                    </Tooltip>
                                </Box>
                            </Grid>
                        </Grid>
                    ))}
                </Grid>
            ) : (
                <CircularProgress sx={{color: '#FFC540'}}></CircularProgress>
            )}
        </>
    );
}